import React from "react";
import {
  FaHandshake,
  FaPaperPlane,
  FaRedo,
  FaTimes,
  FaUserFriends,
} from "react-icons/fa";

function Field({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  error,
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>

      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className={`w-full rounded-2xl border bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:bg-slate-950 dark:text-slate-200 ${
          error
            ? "border-rose-300 dark:border-rose-500/40"
            : "border-slate-200 dark:border-slate-700"
        }`}
      />

      {error ? (
        <p className="mt-1 text-xs text-rose-600 dark:text-rose-300">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export default function ReferralProgramSection({
  showForm,
  setShowForm,
  formData,
  errors,
  loading,
  onChange,
  onSubmit,
  onReset,
}) {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-violet-500 to-[#4CA82F]" />

      <div className="p-5 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Programme parrainage
            </p>

            <h2 className="mt-1 flex items-center gap-3 text-xl font-bold text-slate-900 dark:text-white">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
                <FaHandshake />
              </span>
              Parrainez un club sportif
            </h2>

            <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Recommandez MonAppliClub à un autre club. Si le club parrainé
              souscrit, une réduction peut être appliquée sur votre abonnement.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowForm((value) => !value)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {showForm ? <FaTimes /> : <FaUserFriends />}
            {showForm ? "Masquer le formulaire" : "Parrainer un club"}
          </button>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-5">
          <aside className="lg:col-span-2">
            <div className="h-full rounded-[26px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                Pourquoi parrainer ?
              </h3>

              <div className="mt-4 space-y-3">
                <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Réduction pour votre club
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                    Une remise peut être appliquée sur votre abonnement selon
                    le nombre de clubs parrainés.
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Aide au lancement du club parrainé
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                    Le club recommandé bénéficie d’un contact direct pour
                    découvrir la solution.
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Communauté de clubs
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                    Vous contribuez à faire grandir un réseau de clubs équipés
                    avec des outils simples et adaptés au terrain.
                  </p>
                </div>
              </div>

              <div className="mt-5 overflow-hidden rounded-[22px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <img
                  src="/images/parrainage_02.png"
                  alt="Parrainage"
                  className="h-56 w-full object-cover"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              </div>
            </div>
          </aside>

          {showForm ? (
            <form
              onSubmit={onSubmit}
              className="lg:col-span-3 rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950"
            >
              <div className="mb-5">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  Proposer un club intéressé
                </h3>

                <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Renseignez vos informations, puis celles du club parrainé.
                  Les champs marqués d’un astérisque sont obligatoires.
                </p>
              </div>

              <div className="grid gap-5 xl:grid-cols-2">
                <section className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                  <p className="mb-4 text-sm font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                    Vos informations
                  </p>

                  <div className="space-y-3">
                    <Field
                      label="Club parrain"
                      name="club_parrain"
                      value={formData.club_parrain}
                      onChange={onChange}
                      placeholder="Ex : Judo Club du Bouscat"
                      required
                      error={errors.club_parrain}
                    />

                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field
                        label="Nom"
                        name="nom_parrain"
                        value={formData.nom_parrain}
                        onChange={onChange}
                        placeholder="Nom"
                        required
                        error={errors.nom_parrain}
                      />

                      <Field
                        label="Prénom"
                        name="prenom_parrain"
                        value={formData.prenom_parrain}
                        onChange={onChange}
                        placeholder="Prénom"
                        required
                        error={errors.prenom_parrain}
                      />
                    </div>

                    <Field
                      label="Email"
                      name="email_parrain"
                      type="email"
                      value={formData.email_parrain}
                      onChange={onChange}
                      placeholder="contact@club.fr"
                      required
                      error={errors.email_parrain}
                    />

                    <Field
                      label="Téléphone"
                      name="telephone_parrain"
                      type="tel"
                      value={formData.telephone_parrain}
                      onChange={onChange}
                      placeholder="06..."
                    />
                  </div>
                </section>

                <section className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                  <p className="mb-4 text-sm font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                    Club parrainé
                  </p>

                  <div className="space-y-3">
                    <Field
                      label="Club parrainé"
                      name="club_parraine"
                      value={formData.club_parraine}
                      onChange={onChange}
                      placeholder="Nom du club"
                      required
                      error={errors.club_parraine}
                    />

                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field
                        label="Nom du contact"
                        name="nom_contact_parraine"
                        value={formData.nom_contact_parraine}
                        onChange={onChange}
                        placeholder="Nom"
                      />

                      <Field
                        label="Prénom du contact"
                        name="prenom_contact_parraine"
                        value={formData.prenom_contact_parraine}
                        onChange={onChange}
                        placeholder="Prénom"
                      />
                    </div>

                    <Field
                      label="Email"
                      name="email_parraine"
                      type="email"
                      value={formData.email_parraine}
                      onChange={onChange}
                      placeholder="contact@clubparraine.fr"
                      error={errors.email_parraine}
                    />

                    <Field
                      label="Téléphone"
                      name="telephone_parraine"
                      type="tel"
                      value={formData.telephone_parraine}
                      onChange={onChange}
                      placeholder="06..."
                    />
                  </div>
                </section>
              </div>

              <div className="mt-5 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-xs leading-5 text-indigo-800 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-200">
                Les informations transmises servent uniquement à prendre contact
                au sujet de MonAppliClub et du programme de parrainage.
              </div>

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onReset}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaRedo />
                  Réinitialiser
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                >
                  <FaPaperPlane />
                  {loading ? "Envoi en cours..." : "Envoyer le parrainage"}
                </button>
              </div>
            </form>
          ) : (
            <div className="lg:col-span-3 flex min-h-[260px] items-center justify-center rounded-[26px] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950">
              <div>
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
                  <FaUserFriends />
                </div>

                <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
                  Formulaire masqué
                </h3>

                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                  Cliquez sur “Parrainer un club” pour afficher le formulaire.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}